/**
 * Module Progress Tracking (IndexedDB)
 * Tracks which modules have been viewed and completed
 */

import Dexie, { type Table } from 'dexie';

export interface ModuleProgress {
  moduleId: string;
  viewed: boolean;
  completed: boolean;
  lastViewedAt: string;
  completedAt?: string;
  progress?: number; // 0-100 percentage
}

class ModuleProgressDB extends Dexie {
  moduleProgress!: Table<ModuleProgress, string>;

  constructor() {
    super('ModuleProgressDB');
    this.version(1).stores({
      moduleProgress: 'moduleId, completed, lastViewedAt',
    });
  }
}

const db = new ModuleProgressDB();
const GUEST_USER_ID = 'guest-user';

/**
 * Mark a module as viewed
 */
export async function markModuleViewed(moduleId: string): Promise<void> {
  try {
    const existing = await db.moduleProgress.get(moduleId);
    if (existing) {
      await db.moduleProgress.update(moduleId, {
        viewed: true,
        lastViewedAt: new Date().toISOString(),
      });
    } else {
      await db.moduleProgress.add({
        moduleId,
        viewed: true,
        completed: false,
        lastViewedAt: new Date().toISOString(),
      });
    }
  } catch (error) {
    console.error('Failed to mark module as viewed:', error);
  }
}

/**
 * Mark a module as completed
 */
export async function markModuleCompleted(moduleId: string): Promise<void> {
  try {
    const existing = await db.moduleProgress.get(moduleId);
    if (existing) {
      await db.moduleProgress.update(moduleId, {
        completed: true,
        completedAt: new Date().toISOString(),
        progress: 100,
      });
    } else {
      await db.moduleProgress.add({
        moduleId,
        viewed: true,
        completed: true,
        lastViewedAt: new Date().toISOString(),
        completedAt: new Date().toISOString(),
        progress: 100,
      });
    }
  } catch (error) {
    console.error('Failed to mark module as completed:', error);
  }
}

/**
 * Get progress for a specific module
 */
export async function getModuleProgress(moduleId: string): Promise<ModuleProgress | null> {
  try {
    return (await db.moduleProgress.get(moduleId)) || null;
  } catch (error) {
    console.error('Failed to get module progress:', error);
    return null;
  }
}

/**
 * Get all module progress
 */
export async function getAllModuleProgress(): Promise<ModuleProgress[]> {
  try {
    return await db.moduleProgress.toArray();
  } catch (error) {
    console.error('Failed to get all module progress:', error);
    return [];
  }
}

/**
 * Get completion statistics
 */
export async function getCompletionStats(): Promise<{
  total: number;
  completed: number;
  viewed: number;
}> {
  try {
    const all = await db.moduleProgress.toArray();
    return {
      total: all.length,
      completed: all.filter((m) => m.completed).length,
      viewed: all.filter((m) => m.viewed).length,
    };
  } catch (error) {
    console.error('Failed to get completion stats:', error);
    return { total: 0, completed: 0, viewed: 0 };
  }
}
